import { useField } from 'formik';
import { ChangeEvent, FocusEvent, useMemo, useState } from 'react';

import { Error } from './Error';
import { Info } from './Info';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label: string;
    name: string;
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    className?: string;
    hideValidationIcon?: boolean;
    info?: string;
}

export const Textarea = ({ label, name, id, value, onChange, placeholder, className, info }: TextareaProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const [field, meta] = useField(name);

    const showError = useMemo(() => !!meta.error && meta.touched, [meta.error, meta.touched]);
    const valid = useMemo(() => !meta.error && field.value, [meta.error, field.value]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
        if (!e.target.value) {
            setIsFocused(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="space-y-1">
                <label htmlFor={name}>{label}</label>
                <textarea
                    id={id}
                    name={name}
                    className={`${className} peer w-full border ${
                        showError
                            ? 'border-danger-300'
                            : valid
                            ? 'border-green-300 pr-10'
                            : isFocused
                            ? 'border-blue-400'
                            : 'border-neutral-400'
                    } rounded-lg px-3 py-3 focus:outline-none text-neutral-400 `}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={onChange}
                    value={value}
                    placeholder={placeholder}
                    rows={3}
                />
            </div>
            {showError && <Error>{meta.error}</Error>}
            {info && <Info info={info} />}
        </div>
    );
};
